import { useState } from 'react'

import {
  BATCH_EVENT_LEDGERS,
  fetchAllEventHistory,
  type FetchAllHistoryResult,
  type HistoryLedger,
} from '@ctcells/api'
import type { Batch } from '@ctcells/contract'
import {
  EVENT_LOG_EXPORT_MAX_PAGES,
  EVENT_LOG_EXPORT_PAGE_SIZE,
  platform,
  type Permission,
} from '@ctcells/core'
import { enumLabel, errorMessage, intlLocale, t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { viewGate } from '../access.ts'
import { batchDisplayName } from './batchDisplayName.ts'
import { buildEventLogCsv, eventLogFileName } from './eventLogCsv.ts'

import './eventLogDownload.css'

/**
 * 「이벤트 로그 내려받기」 — 작업 하나의 `GET /history?batch_id=` 를 끝까지 읽어 CSV 로 저장한다(eric 2026-10-02
 * 「작업이 완료되거나 중단된 거.. 이벤트 로그를 뽑아 볼 수 있어?」 · 「이벤트 로그 실행 중인 것도 버튼 만들어줘」).
 *
 * 🔴 **상태와 무관하게 한 버튼이다** — 완료 · 중지 · 실패 · 실행 중 · 대기 모두. 도는 작업은 **누른 시각까지**다
 *    (그 시각을 서버 `to` 로 박고 파일 이름 · 머리에 적는다 · `fetchAllHistory` 머리말).
 * 🔴 **「이벤트」 원장 둘이다**(`event` · `batch_record` — 팀장 결정 2026-10-02 · eric 「이벤트 로그」). 2026-10-08(v0.9.4)
 *    서버가 배치 기록을 `event_log` 에서 `batch_record` 로 뗐다 — 옛 작업은 `event`, 새 작업은 `batch_record` 에 있어 둘 다
 *    읽는다(`@ctcells/api` `fetchAllEventHistory`). 🔴 **감사 원장도 받는다**(팀장 결정 2026-10-11 · `BATCH_EVENT_LEDGERS`) —
 *    사람이 누른 시작 · 중지는 거기에만 있다. 명령 원장은 받지 않는다(`target` 에 배치 접두가 없어 작업으로 못 거른다).
 *    거르는 것은 서버다(`services/history.py` `list_`).
 * 🔴 **저장은 플랫폼 포트 뒤다**(`platform.files.save`) — 화면이 `Blob` · `a[download]` 를 만들지 않는다.
 *    웹은 브라우저 내려받기, 데스크톱 셸은 저장 대화 + 파일 쓰기(구현은 셸이 붙을 때)다.
 *
 * 경계
 * | 권한 없음 | `view` 가 없으면(viewer) **요청을 보내지 않는다** — 비활성 + 사유(`/history` 는 operator · admin 한정) |
 * | 받는 중   | 버튼 비활성 + 「받는 중」. 두 번 누르기를 막는다                                         |
 * | 실패      | 사유 한 줄(서버 에러 코드 문구 · `errorMessage`). 다음 누름에 지워진다                    |
 * | 기록 없음 | 빈 파일을 내지 않고 「기록 없음」 한 줄                                                  |
 * | 너무 많음 | 안전장치(`EVENT_LOG_EXPORT_MAX_PAGES`)에 걸리면 **잘린 파일을 내지 않고** 사유 한 줄      |
 * | 연결 끊김 | 막지 않는다 — 읽기이고 과거 기록이다(`viewGate` 는 stale 게이트를 걸지 않는다)            |
 * | 목 자료   | 보낼 곳이 없다 — 비활성 + 사유(`reason.demoSource`). 목 화면이 실서버를 때리지 않는다      |
 */
export interface EventLogDownloadProps {
  batch: Pick<Batch, 'batch_id' | 'status'> & { name?: string | null; recipe_id?: string | null }
  /** 서버가 준 보유 권한(`usePermissions`) */
  permissions: readonly Permission[] | undefined
  /** `false` 면 목 자료다 — 버튼이 비활성 + 사유로 선다 */
  live: boolean
  label?: string | undefined
  ledgers?: readonly HistoryLedger[] | undefined
  /** 시험 · 하네스가 갈아 끼운다. 기본은 `@ctcells/api` `fetchAllEventHistory` */
  fetchAll?: typeof fetchAllEventHistory
  /** 시험이 「지금」을 고정한다 */
  now?: () => Date
}

type Phase =
  { kind: 'idle' } | { kind: 'pending' } | { kind: 'empty' } | { kind: 'failed'; text: string }

function truncatedText(result: Extract<FetchAllHistoryResult, { kind: 'truncated' }>): string {
  return t('eventLog.truncated', { fetched: result.fetched.toLocaleString(intlLocale()) })
}

export function EventLogDownload({
  batch,
  permissions,
  live,
  label = t('action.eventLogDownload'),
  ledgers = BATCH_EVENT_LEDGERS,
  fetchAll,
  now = () => new Date(),
}: EventLogDownloadProps) {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const pending = phase.kind === 'pending'

  const permissionGate = viewGate({ permissions }, 'view')
  const gateProps = !live
    ? ({ disabled: true, disabledReason: t('reason.demoSource') } as const)
    : permissionGate.disabled
      ? permissionGate
      : pending
        ? ({ disabled: true, disabledReason: t('eventLog.downloading') } as const)
        : ({ disabled: false } as const)

  const download = async () => {
    setPhase({ kind: 'pending' })
    const asOf = now()
    try {
      // ⚠ 누를 때 고른다 — 렌더마다 기본값으로 읽으면 `@ctcells/api` 를 갈아 끼운 시험이 렌더에서 죽는다
      const result = await (fetchAll ?? fetchAllEventHistory)(
        { batch_id: batch.batch_id, to: asOf.toISOString(), ledgers },
        { pageSize: EVENT_LOG_EXPORT_PAGE_SIZE, maxPages: EVENT_LOG_EXPORT_MAX_PAGES },
      )
      if (result.kind === 'truncated') {
        setPhase({ kind: 'failed', text: truncatedText(result) })
        return
      }
      if (result.items.length === 0) {
        setPhase({ kind: 'empty' })
        return
      }
      const batchName = batchDisplayName(batch)
      const csv = buildEventLogCsv(result.items, {
        batchName,
        batchId: batch.batch_id,
        statusLabel: enumLabel('enum.batchStatus', batch.status),
        asOf,
      })
      await platform.files.save({
        fileName: eventLogFileName(batchName, batch.batch_id, asOf),
        mimeType: 'text/csv;charset=utf-8',
        data: csv,
      })
      setPhase({ kind: 'idle' })
    } catch (error: unknown) {
      setPhase({
        kind: 'failed',
        text: errorMessage(error as Parameters<typeof errorMessage>[0]),
      })
    }
  }

  return (
    <span className="eventlog-dl">
      <Button
        onClick={() => {
          void download()
        }}
        size="pill"
        /* 주 버튼(테마색 채움 · 흰 글자) — eric 2026-10-14 「오른쪽 정렬 파란색(테마색)으로.. 글자색은 흰색」 */
        variant="primary"
        {...gateProps}
      >
        {pending ? t('eventLog.downloading') : label}
      </Button>
      {phase.kind === 'empty' ? (
        <span className="eventlog-dl__note muted" role="status">
          {t('eventLog.empty')}
        </span>
      ) : null}
      {phase.kind === 'failed' ? (
        <span className="eventlog-dl__note eventlog-dl__note--failed" role="alert">
          {phase.text}
        </span>
      ) : null}
    </span>
  )
}
